/**
 * Typed REST endpoint helpers built on top of `ApiClient`.
 *
 * Each function maps directly to a Lambda route in `cdk-backend/lambda/...`
 * and validates the response against the matching zod schema, so a Python ↔
 * TypeScript contract drift is detected at the call site (not deep inside
 * a component's render).
 */

import { z } from "zod";

import {
  AnalysisListSchema,
  AnalysisSchema,
  BiomarkerListSchema,
  BiomarkerEntrySchema,
  ConversationSchema,
  DashboardSchema,
  DocumentSchema,
  FactDeletedSchema,
  FactSchema,
  PasskeySchema,
  ProfileSchema,
  ProtocolListSchema,
  ProtocolSchema,
  RecommendationSchema,
  ShoppingHistorySchema,
  CoachPhotoSchema,
  SymptomSchema,
  TrackingDaySchema,
  cursorList,
} from "../schemas";
import type {
  BiomarkerCreateSchema,
  DocumentCreateSchema,
  FactCreateSchema,
  ProfileUpsertSchema,
  SymptomCreateSchema,
  SymptomUpdateSchema,
} from "../schemas";
import type { ApiClient } from "./client";

const Empty: z.ZodType<null> = z
  .unknown()
  .transform((): null => null) as z.ZodType<null>;

const PasskeyListSchema = z.object({ items: z.array(PasskeySchema) });
const SymptomListSchema = cursorList(SymptomSchema);
const DocumentListSchema = cursorList(DocumentSchema);
const ConversationListSchema = cursorList(ConversationSchema);
const FactListSchema = cursorList(FactSchema);

// Lenient schema for loading a conversation's messages into the chat window.
// Strict parsing previously threw an ApiError on any unexpected message field
// and blanked the chat; this tolerates missing/extra fields so messages always
// load.
const ConversationMessagesResponseSchema = z
  .object({
    messages: z
      .array(
        z
          .object({
            id: z.string(),
            convId: z.string().optional(),
            conversationId: z.string().optional(),
            role: z.string().default("assistant"),
            content: z.string().default(""),
            citations: z
              .array(
                z
                  .object({
                    source: z.string().default(""),
                    url: z.string().nullable().optional(),
                  })
                  .passthrough(),
              )
              .default([]),
            toolUses: z
              .array(
                z
                  .object({
                    name: z.string().default(""),
                    id: z.string().nullable().optional(),
                  })
                  .passthrough(),
              )
              .default([]),
            createdAt: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();

const PasskeyRegisterStartResponse = z.object({
  credentialCreationOptions: z.unknown(),
  pendingName: z.string(),
});

const PasskeyRegisterCompleteResponse = z.object({
  credentialId: z.string(),
  name: z.string(),
});

export interface PaginationParams {
  cursor?: string | null;
  limit?: number;
}

const buildQuery = (params?: PaginationParams): string => {
  if (!params) return "";
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const q = sp.toString();
  return q ? `?${q}` : "";
};

export class HealthApi {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  // -- profile -----------------------------------------------------------
  getProfile = (): Promise<z.infer<typeof ProfileSchema>> =>
    this.client.get("/profile", ProfileSchema);

  putProfile = (
    body: z.infer<typeof ProfileUpsertSchema>,
  ): Promise<z.infer<typeof ProfileSchema>> =>
    this.client.put("/profile", body, ProfileSchema);

  postQuestionnaire = (body: {
    questionId?: string;
    answer?: string;
  }): Promise<unknown> =>
    this.client.post("/profile/questionnaire", body, z.unknown());

  // -- facts (optional health inputs) -----------------------------------
  /**
   * List facts for one health-input category (e.g. ``medication``,
   * ``allergy``, ``immunization``). The backend filters by both the caller
   * and the category server-side. Returns a cursor-paginated list.
   */
  listFacts = (
    category: string,
    params?: PaginationParams,
  ): Promise<z.infer<typeof FactListSchema>> => {
    const sp = new URLSearchParams();
    sp.set("category", category);
    if (params?.cursor) sp.set("cursor", params.cursor);
    if (params?.limit) sp.set("limit", String(params.limit));
    return this.client.get(`/profile/facts?${sp.toString()}`, FactListSchema);
  };

  createFact = (
    body: z.infer<typeof FactCreateSchema>,
  ): Promise<z.infer<typeof FactSchema>> =>
    this.client.post("/profile/facts", body, FactSchema);

  deleteFact = (id: string): Promise<z.infer<typeof FactDeletedSchema>> =>
    this.client.delete(
      `/profile/facts/${encodeURIComponent(id)}`,
      FactDeletedSchema,
    );

  // -- symptoms ----------------------------------------------------------
  listSymptoms = (
    params?: PaginationParams,
  ): Promise<z.infer<typeof SymptomListSchema>> =>
    this.client.get(`/symptoms${buildQuery(params)}`, SymptomListSchema);

  createSymptom = (
    body: z.infer<typeof SymptomCreateSchema>,
  ): Promise<z.infer<typeof SymptomSchema>> =>
    this.client.post("/symptoms", body, SymptomSchema);

  updateSymptom = (
    id: string,
    body: z.infer<typeof SymptomUpdateSchema>,
  ): Promise<z.infer<typeof SymptomSchema>> =>
    this.client.patch(
      `/symptoms/${encodeURIComponent(id)}`,
      body,
      SymptomSchema,
    );

  deleteSymptom = (id: string): Promise<null> =>
    this.client.delete(`/symptoms/${encodeURIComponent(id)}`, Empty);

  // -- documents ---------------------------------------------------------
  listDocuments = (): Promise<z.infer<typeof DocumentListSchema>> =>
    this.client.get("/documents", DocumentListSchema);

  createDocument = (
    body: z.infer<typeof DocumentCreateSchema>,
  ): Promise<z.infer<typeof DocumentSchema>> =>
    this.client.post("/documents", body, DocumentSchema);

  getDocument = (id: string): Promise<z.infer<typeof DocumentSchema>> =>
    this.client.get(`/documents/${encodeURIComponent(id)}`, DocumentSchema);

  deleteDocument = (id: string): Promise<null> =>
    this.client.delete(`/documents/${encodeURIComponent(id)}`, Empty);

  retryDocument = (id: string): Promise<z.infer<typeof DocumentSchema>> =>
    this.client.post(
      `/documents/${encodeURIComponent(id)}/retry`,
      {},
      DocumentSchema,
    );

  // -- dashboard ---------------------------------------------------------
  getDashboard = (): Promise<z.infer<typeof DashboardSchema>> =>
    this.client.get("/dashboard", DashboardSchema);

  // -- conversations -----------------------------------------------------
  listConversations = (
    params?: PaginationParams,
  ): Promise<z.infer<typeof ConversationListSchema>> =>
    this.client.get(
      `/conversations${buildQuery(params)}`,
      ConversationListSchema,
    );

  createConversation = (
    mode: "smart" | "advanced",
  ): Promise<z.infer<typeof ConversationSchema>> =>
    this.client.post("/conversations", { mode }, ConversationSchema);

  getConversation = (id: string) =>
    this.client.get(
      `/conversations/${encodeURIComponent(id)}`,
      ConversationMessagesResponseSchema,
    );

  renameConversation = (id: string, title: string): Promise<unknown> =>
    this.client.patch(
      `/conversations/${encodeURIComponent(id)}`,
      { title },
      z.unknown(),
    );

  deleteConversation = (id: string): Promise<null> =>
    this.client.delete(`/conversations/${encodeURIComponent(id)}`, Empty);

  // Buffered fallback for environments where WebSocket is unavailable.
  postMessage = (
    id: string,
    body: { content: string; mode: "smart" | "advanced"; imageKeys?: string[] },
  ): Promise<unknown> =>
    this.client.post(
      `/conversations/${encodeURIComponent(id)}/messages`,
      body,
      z.unknown(),
    );

  // -- passkeys ----------------------------------------------------------
  listPasskeys = (): Promise<z.infer<typeof PasskeyListSchema>> =>
    this.client.get("/auth/passkeys", PasskeyListSchema);

  startPasskeyRegistration = (
    name: string,
  ): Promise<z.infer<typeof PasskeyRegisterStartResponse>> =>
    this.client.post(
      "/auth/passkeys",
      { action: "start", name },
      PasskeyRegisterStartResponse,
    );

  completePasskeyRegistration = (
    name: string,
    credential: unknown,
  ): Promise<z.infer<typeof PasskeyRegisterCompleteResponse>> =>
    this.client.post(
      "/auth/passkeys",
      { action: "complete", name, credential },
      PasskeyRegisterCompleteResponse,
    );

  renamePasskey = (
    id: string,
    name: string,
  ): Promise<z.infer<typeof PasskeySchema>> =>
    this.client.patch(
      `/auth/passkeys/${encodeURIComponent(id)}`,
      { name },
      PasskeySchema,
    );

  deletePasskey = (id: string): Promise<null> =>
    this.client.delete(`/auth/passkeys/${encodeURIComponent(id)}`, Empty);

  // -- account -----------------------------------------------------------
  deleteAccount = (): Promise<null> => this.client.delete("/account", Empty);

  // -- full analysis -----------------------------------------------------
  createAnalysis = () =>
    this.client.post("/analysis", undefined, AnalysisSchema);

  listAnalyses = (): Promise<z.infer<typeof AnalysisListSchema>> =>
    this.client.get("/analysis", AnalysisListSchema);

  getAnalysis = (version: number) =>
    this.client.get(`/analysis/${version}`, AnalysisSchema);

  getRecommendationDetail = (
    version: number,
    recId: string,
  ): Promise<z.infer<typeof RecommendationSchema>> =>
    this.client.post(
      `/analysis/${version}/recommendations/${encodeURIComponent(recId)}/detail`,
      undefined,
      RecommendationSchema,
    );

  // -- genetic health protocol -------------------------------------------
  createProtocol = () =>
    this.client.post("/protocol", undefined, ProtocolSchema);

  listProtocols = () => this.client.get("/protocol", ProtocolListSchema);

  getProtocol = (version: number) =>
    this.client.get(`/protocol/${version}`, ProtocolSchema);

  submitCabinet = (
    version: number,
    body: { items?: unknown[]; photoKeys?: string[] },
  ): Promise<unknown> =>
    this.client.post(`/protocol/${version}/cabinet`, body, z.unknown());

  // -- daily tracking ----------------------------------------------------
  logSupplement = (body: {
    name: string;
    taken?: boolean;
    scheduleItemId?: string;
  }): Promise<unknown> =>
    this.client.post("/tracking/supplements", body, z.unknown());

  logMeal = (body: {
    text?: string;
    photoKey?: string;
    macros?: Record<string, number>;
  }): Promise<unknown> =>
    this.client.post("/tracking/meals", body, z.unknown());

  getTrackingDay = (date?: string) =>
    this.client.get(
      date ? `/tracking?date=${encodeURIComponent(date)}` : "/tracking",
      TrackingDaySchema,
    );

  // -- shopping advisor --------------------------------------------------
  adviseShopping = (body: {
    photoKey?: string;
    productName?: string;
  }): Promise<unknown> =>
    this.client.post("/shopping/advise", body, z.unknown());

  shoppingHistory = () =>
    this.client.get("/shopping/history", ShoppingHistorySchema);

  // -- coaching photo presign (meal / cabinet label / product) -----------
  presignCoachPhoto = (body: { filename: string; contentType: string }) =>
    this.client.post("/tracking/photo", body, CoachPhotoSchema);

  // -- manual biomarker entry (BIO# records) -----------------------------
  listBiomarkers = () => this.client.get("/biomarkers", BiomarkerListSchema);

  createBiomarker = (body: z.infer<typeof BiomarkerCreateSchema>) =>
    this.client.post("/biomarkers", body, BiomarkerEntrySchema);

  deleteBiomarker = (key: string): Promise<null> =>
    this.client.delete(`/biomarkers/${encodeURIComponent(key)}`, Empty);
}
